import { FC, useEffect } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { AddToCollectionModal } from "@/components/modal/add-to-collection/add-to-collection-modal";
import { SearchPageHeader } from "@/pages/search/SearchPageHeader";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import { ShareSheetModal } from "@/components/modal/share-sheet/share-sheet-modal";
import { SearchPageNotesList } from "./components/SearchPageNotesList";
import { MdsPanelContainer } from "@/design-system/components/panel";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { SearchPageFloaty } from "@/pages/search/components/SearchPageFloaty";
import { useTabLocationState } from "@/hooks/useTabLocationState";
import { useTabContext } from "@/components/layout/TabContext";
import { useAppStore } from "@/store";

interface SearchPageProps {}

export const SearchPage: FC<SearchPageProps> = observer(() => {
  const { store } = useAppStore();
  const page = useTabPageStore("searchPage");
  page.listState.useListStateEffects();

  const queryString = page.filters.params.queryString;
  /**
   * Track views of the search page.
   */
  useEffect(() => {
    if (queryString) {
      trackEvent(TrackedEvent.SearchView, {
        query: queryString,
      });
    }
  }, [queryString]);

  // Retrigger search when navigation sets the retrigger flag, then clear the flag
  const { activeTab } = useTabContext();
  const routerLocationState = useTabLocationState<{ retriggerSearch?: boolean }>();
  useEffect(() => {
    const { retriggerSearch } = routerLocationState || {};
    if (!retriggerSearch) return;
    page.refreshSearchEngineQuery(page.filters.searchQuery);
    store.navigation.tabs.clearTabState(activeTab.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocationState]);

  return (
    <Wrapper data-test-id="search-page">
      <ShareSheetModal store={page.shareSheetModal} />
      <AddToCollectionModal store={page.addToCollectionModal} />
      <SearchPageHeader />
      <MdsPanelContainer>
        <SearchPageNotesList />
        {page.listState.multiSelectUi}
        <SearchPageFloaty />
      </MdsPanelContainer>
    </Wrapper>
  );
});

const Wrapper = styled.div({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
});
